import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { vectorMin } from '../assets/assets';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '../services/category.service';

const Services = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);
  const sliderRef = useRef(null);

  // Fetch Categories from API
  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });

  // Filter categories that have images (optional, based on design requirements)
  // or just use all categories. The prompt says "Get unique category IDs from gigs" in original code
  // but we want to show all categories that the backend returns now?
  // Backend returns gigCount, so we can filter by gigCount > 0 if we only want active ones.
  // "Get active categories based on IDs" was the old logic.
  // New logic: Use categories from API.
  const categories = categoriesData?.categories || [];
  
  // Filter only categories with gigs if desired, or show all.
  // Let's show all for now, or maybe only those with gigCount > 0 if we want "active" only.
  // But usually you want to show empty categories too so people can see what's possible
  // OR the prompt implied "active categories". Let's stick to showing all for now but 
  // maybe we can sort by gigCount desc?
  // The prompt said: "display gigs based on category in params everyting on the oage should be functional"
  
  const activeCategories = categories;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      // Calculate max index based on visible items
      if (!mobile && sliderRef.current) {
        const maxScrollableIndex = Math.max(0, activeCategories.length - 4);
        setMaxIndex(maxScrollableIndex);
      }
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [activeCategories.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex >= maxIndex ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? maxIndex : prevIndex - 1
    );
  };

  const handleCategoryClick = (category) => {
    // Use slug if available, otherwise ID (though slug should be there)
    const slug = category.slug || category._id;
    window.location.href = `/category/${slug}`;
  };

  if (isLoading) {
    return <div className="py-16 text-center">
      <div className="flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    </div>;
  }

  // Fallback image if thumbnail is missing
  const getThumbnail = (category) => {
    return category.thumbnail || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"; // Placeholder
  };
  
  // Fallback icon logic if needed, but API should provide what we need. 
  // Note: Old code had `isIconImg`. Our backend might just return `icon` generic string or URL.
  // We'll assume `icon` is a string (emoji/text) or url.
  // Actually the model says `icon` is String.
  
  // For mobile view, show categories in grid layout
  const MobileView = () => (
    <div className="px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">
          <span className="text-gray-900">SERVICES</span>{" "}
          <span className="text-[#4B718F]">WE OFFER</span>
        </h2>
        <a href="/services" className="flex items-center text-[#174568] font-medium">
          See All <img src={vectorMin} className='ml-2' alt="See all arrow" />
        </a>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {activeCategories.length > 0 ? (
          activeCategories.slice(0, 4).map((category) => (
            <div
              key={category._id}
              className="flex flex-col cursor-pointer"
              onClick={() => handleCategoryClick(category)}
            >
              <div className="relative bg-gray-800 rounded-lg overflow-hidden aspect-square mb-2">

                <img
                  src={getThumbnail(category)}
                  alt={category.name}
                  className="w-full h-full object-cover opacity-60"
                />

                <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-4">
                  {/* Assuming icon is an image URL or emoji string. Detecting if it's a URL is tricky without regex 
                      but let's assume it's like the category name or simple string for now if not valid URL 
                  */}
                  {category.icon && category.icon.includes('/') ? (
                    <img
                      src={category.icon}
                      alt={category.name}
                      className="w-[2rem] h-[2rem] object-cover opacity-60"
                    />
                  ) : (
                    <div className="text-3xl mb-2">{category.icon || "🔧"}</div>
                  )}
                  <div className="font-bold text-sm">{category.name}</div>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-bold">{category.name}</p>
                  <p className="text-gray-500 text-sm">{category.gigCount || 0} Gigs</p>
                </div>
                <ArrowRight size={18} className="text-gray-500" />
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-2 text-center py-8">No categories found</div>
        )}
      </div>
    </div>
  );

  // For desktop view, show as a carousel
  const DesktopView = () => (
    <div className="px-12 py-16">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold">
          <span className="text-gray-900">SERVICES</span>{" "}
          <span className="text-[#4B718F]">WE OFFER</span>
        </h2>
        <a href="/services" className="flex items-center text-[#174568] font-medium">
          See All <img src={vectorMin} className='ml-1' alt="See all arrow" />
        </a>
      </div>

      {activeCategories.length > 0 ? (
        <div className="relative">
          {activeCategories.length > 4 && (
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -ml-10 bg-white rounded-full p-3 shadow-md z-10 hover:bg-gray-100 transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          <div
            ref={sliderRef}
            className="w-full overflow-hidden"
          >
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * 25}%)`
              }}
            >
              {activeCategories.map((category) => (
                <div
                  key={category._id}
                  className="w-1/4 flex-shrink-0 px-3 cursor-pointer"
                  onClick={() => handleCategoryClick(category)}
                >
                  <div className="w-full h-64 relative bg-gray-800 rounded-lg overflow-hidden mb-4">

                    <img
                      src={getThumbnail(category)}
                      alt={category.name}
                      className="w-full h-full object-cover opacity-60"
                    />

                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-4">

                      {category.icon && category.icon.includes('/') ? (
                        <img
                          src={category.icon}
                          alt={category.name}
                          className="w-[3rem] h-[3rem] object-cover opacity-60"
                        />
                      ) : (
                         <div className="text-4xl mb-3">{category.icon || "🔧"}</div>
                      )}

                      <div className="font-bold">{category.name}</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold">{category.name}</p>
                      <p className="text-gray-500">{category.gigCount || 0} Gigs</p>
                    </div>
                    <ArrowRight size={20} className="text-gray-500" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {activeCategories.length > 4 && (
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 -mr-10 bg-white rounded-full p-3 shadow-md z-10 hover:bg-gray-100 transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      ) : (
        <div className="text-center py-8">No categories found</div>
      )}
    </div>
  );

  return isMobile ? <MobileView /> : <DesktopView />;
};

export default Services; 